import React, { useEffect } from 'react';

import { Card, Grid, Stack, Typography, useTheme, Box } from '@mui/material';
import { componentLogger } from '@ragu-components/logger';
import { OrdIconStandard } from '@ragu-components/ord-components';
import { canAccess } from '@ratto-components/ra-rbac';
import { Link, usePermissions, useTranslate } from 'react-admin';

import { useIngordDataProvider } from 'src/components/flowchart/hooks/useIngordDataProvider';
import { useVisitedResourcesStore } from 'src/hooks/useVisitedResources';

import type { SxProps, Theme } from '@mui/material';

const resourcesIcons = {
  users: 'users',
  groups: 'groups',
  queues: 'queues',
  labels: 'labels',
  contacts: 'contacts',
} as const;

const ResourceCard = ({
  resource,
  order,
  canList,
  canCreate,
}: {
  resource: keyof typeof resourcesIcons;
  order: number;
  canList: boolean;
  canCreate: boolean;
}): React.ReactElement => {
  const [total, setTotal] = React.useState(0);

  const theme = useTheme();
  const translate = useTranslate();
  const dataProvider = useIngordDataProvider();

  useEffect(() => {
    if (!canList) {
      return;
    }

    dataProvider
      .getList(resource, {
        filter: {},
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'ASC' },
      })
      .then(({ total }) => {
        setTotal(total ?? 0);
      })
      .catch((error) => {
        componentLogger.error(`Error fetching ${resource} count`, error);
      });
  }, [canList, dataProvider, resource]);

  return (
    <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={resource} sx={{ order }}>
      <HomeCard sx={{ height: '100%' }}>
        <Stack direction={'row'} padding={theme.spacing(2)} alignItems='center'>
          {resource in resourcesIcons ? <OrdIconStandard name={resourcesIcons[resource]} /> : null}
          <Stack sx={{ flexGrow: 1, paddingX: 2 }}>
            <Typography gutterBottom variant='h6'>
              {translate(`resources.${resource}.name`, { _: resource })}
            </Typography>
            <Typography variant='subtitle1' color='text.secondary'>
              {translate(`ord.home.resourceDescription`, { resource })}
            </Typography>
          </Stack>
          <Typography variant='h4'>{total}</Typography>
        </Stack>
        <Stack sx={{ flexDirection: 'column', alignItems: 'flex-start', marginLeft: 2, marginBottom: 1 }}>
          {canList && <Link to={`/${resource}`}>{translate(`resources.${resource}.operations.edit`)}</Link>}
          {canCreate && <Link to={`/${resource}/create`}>{translate(`resources.${resource}.operations.create`)}</Link>}
        </Stack>
      </HomeCard>
    </Grid>
  );
};

export const Header = (): React.ReactElement => {
  const theme = useTheme();
  const translate = useTranslate();
  return (
    <Card
      sx={{
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Stack
        direction='row'
        justifyContent='space-between'
        padding={theme.spacing(3)}
        sx={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/bg-hompage.png)`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: '100% auto',
          minHeight: 270,
        }}
      >
        <Stack>
          <Typography gutterBottom variant='h6'>
            {translate('ord.home.title')}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {translate('ord.home.description')}
          </Typography>
        </Stack>
      </Stack>
    </Card>
  );
};

export const HomeCard = ({ children, sx }: React.PropsWithChildren<{ sx?: SxProps<Theme> }>): React.ReactElement => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        backgroundColor: theme.palette.background.paper,
        ...sx,
      }}
    >
      {children}
    </Card>
  );
};

export const HomePage = (): React.ReactElement => {
  const theme = useTheme();
  const [visitedResources] = useVisitedResourcesStore();
  const translate = useTranslate();
  const { permissions } = usePermissions();

  const visibleResources = Object.keys(resourcesIcons).flatMap((resource) => {
    const canList = canAccess({ permissions, action: 'list', resource });
    const canCreate = canAccess({ permissions, action: 'create', resource });

    if (canList || canCreate) {
      return [{ resource, canList, canCreate }];
    }
    return [];
  });

  const visibleCount = visibleResources.length;

  // resourcecard have 10, 20, 30... positions, so visitedResources should have 5, 15, 25...
  const getPosition = (cols: number): number => {
    const total = visibleCount + 1;
    if (total <= cols) {
      // 5 -> first position (0 is the HomeCard)
      return 5;
    }
    // position of the first place of the last row
    const position = Math.floor(visibleCount / cols) * cols;
    return position * 10 + 5;
  };

  const smPosition = getPosition(2);
  const lgPosition = getPosition(3);

  return (
    <Grid
      container
      sx={{
        maxHeight: 'calc(100vh - 50px)',
        paddingBottom: 1,
        paddingTop: 1.5,
        [theme.breakpoints.down('md')]: { overflow: 'auto' },
      }}
      spacing={4}
    >
      <Grid size={12}>
        <Header />
      </Grid>
      {Object.entries(visitedResources).length > 0 && (
        <Grid
          size={{ xs: 12, sm: 6, lg: 4 }}
          data-testid='home-most-visited'
          sx={{
            order: 5,
            [theme.breakpoints.up('sm')]: { order: smPosition },
            [theme.breakpoints.up('lg')]: { order: lgPosition },
            position: 'relative',
          }}
        >
          <HomeCard sx={{ [theme.breakpoints.up('lg')]: { position: 'absolute' }, width: '100%' }}>
            <Box sx={{ padding: theme.spacing(2) }}>
              <Typography gutterBottom variant='h6'>
                {translate('ord.home.mostVisited')}
              </Typography>
              <Stack sx={{ [theme.breakpoints.down('lg')]: { flexDirection: 'row', flexWrap: 'wrap' } }}>
                {Object.entries(visitedResources)
                  .sort(([_resourceA, countA], [_resourceB, countB]) => countB - countA)
                  .slice(0, 6)
                  .map(([resource]) => (
                    <Link key={resource} to={`/${resource}`} sx={{ [theme.breakpoints.down('lg')]: { width: '50%' } }}>
                      {translate(`resources.${resource}.name`, { _: resource })}
                    </Link>
                  ))}
              </Stack>
            </Box>
          </HomeCard>
        </Grid>
      )}
      {visibleResources.map(({ resource, canList, canCreate }, index) => (
        <ResourceCard
          resource={resource as keyof typeof resourcesIcons}
          canList={canList}
          canCreate={canCreate}
          key={resource}
          order={(index + 1) * 10}
        />
      ))}
    </Grid>
  );
};
